// Service for grouping and organizing anime by relations
import { getTotalEpisodes, getTotalChapters, getTotalVolumes } from '../utils/timeFormatter';
import { OP_ED_SKIP_MINUTES, MANGA_FORMATS } from '../config/constants';
import { getDisplayTitle } from '../hooks/useDisplayTitle';

/**
 * Parse date object to timestamp for sorting
 * @param {Object} dateObj - AniList date object {year, month, day}
 * @returns {number} Timestamp or Infinity if no date
 */
const parseDate = (dateObj) => {
    if (!dateObj || !dateObj.year) return Infinity;

    const year = dateObj.year;
    const month = dateObj.month || 1;
    const day = dateObj.day || 1;

    return new Date(year, month - 1, day).getTime();
};

/**
 * Get formatted release date string
 * @param {Object} dateObj - AniList date object
 * @returns {string} Formatted date (e.g., "2013" or "Apr 2013")
 */
const formatReleaseDate = (dateObj) => {
    if (!dateObj || !dateObj.year) return 'TBA';

    const year = dateObj.year;
    if (!dateObj.month) return `${year}`;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[dateObj.month - 1];

    return `${month} ${year}`;
};

/**
 * Build a graph of anime relations
 * @param {Array} animeList - User's anime entries
 * @param {Object} relationsData - Relations data from API
 * @returns {Object} Graph with adjacency list
 */
const buildRelationGraph = (animeList, relationsData) => {
    const graph = {};
    const animeMap = {};

    // Build anime map for quick lookup - DEDUPLICATE by mediaId
    // If same anime appears multiple times (e.g., rewatching), keep the first occurrence
    animeList.forEach(entry => {
        const mediaId = entry.media.id;

        // Only add if not already in map (prevents duplicates)
        if (!animeMap[mediaId]) {
            animeMap[mediaId] = entry;
            graph[mediaId] = new Set();
        }
    });

    // Add edges based on relations - BIDIRECTIONAL for all types
    // BUT filter out unwanted relation types and formats FOR GROUPING
    Object.keys(relationsData).forEach(mediaId => {
        const relations = relationsData[mediaId]?.relations?.edges || [];

        relations.forEach(edge => {
            const relatedId = edge.node.id;
            const relationType = edge.relationType;
            const relatedFormat = edge.node.format;

            // Filter out unwanted relation types for GROUPING
            if (relationType === 'CHARACTER') return;
            if (relationType === 'SUMMARY') return;
            if (relationType === 'COMPILATION') return;
            if (relationType === 'OTHER') return;

            // Filter out non-anime formats
            if (relatedFormat === 'MUSIC') return;
            if (relatedFormat === 'MANGA') return;
            if (relatedFormat === 'NOVEL') return;
            if (relatedFormat === 'ONE_SHOT') return;

            // Only add edge if BOTH anime are in user's list
            if (animeMap[mediaId] && animeMap[relatedId]) {
                // Create bidirectional edge (undirected graph)
                // This ensures PARENT/CHILD, PREQUEL/SEQUEL etc. all connect properly
                graph[mediaId].add(relatedId);
                graph[relatedId].add(mediaId);
            }
        });
    });

    return { graph, animeMap };
};

/**
 * Find connected components (groups) using DFS
 * @param {Object} graph - Adjacency list graph
 * @returns {Array<Array>} Array of groups (each group is array of media IDs)
 */
const findConnectedComponents = (graph) => {
    const visited = new Set();
    const groups = [];

    const dfs = (nodeId, currentGroup) => {
        // Convert to string for consistent comparison
        const nodeIdStr = String(nodeId);
        visited.add(nodeIdStr);
        currentGroup.push(parseInt(nodeIdStr)); // Store as number

        const neighbors = graph[nodeId] || new Set();
        neighbors.forEach(neighborId => {
            const neighborIdStr = String(neighborId);
            if (!visited.has(neighborIdStr)) {
                dfs(neighborId, currentGroup);
            }
        });
    };

    Object.keys(graph).forEach(nodeId => {
        const nodeIdStr = String(nodeId);
        if (!visited.has(nodeIdStr)) {
            const group = [];
            dfs(nodeId, group);
            if (group.length > 0) {
                groups.push(group);
            }
        }
    });

    return groups;
};

/**
 * Sort group by release date
 * @param {Array} group - Array of anime entries
 * @returns {Array} Sorted array
 */
export const sortGroupByReleaseDate = (group) => {
    return [...group].sort((a, b) => {
        const dateA = parseDate(a.media?.startDate);
        const dateB = parseDate(b.media?.startDate);
        return dateA - dateB;
    });
};

/**
 * Get group name (title of earliest anime)
 * @param {Array} group - Sorted array of anime entries
 * @returns {string} Group name
 */
export const getGroupName = (group, titlePreference = null) => {
    if (!group || group.length === 0) return 'Unknown Group';

    const sorted = sortGroupByReleaseDate(group);
    return getDisplayTitle(sorted[0].media, titlePreference);
};

/**
 * Calculate group statistics
 * @param {Array} group - Array of anime entries
 * @returns {Object} Statistics {totalEpisodes, totalDuration, animeCount, watchedHours, remainingHours}
 */
export const calculateGroupStats = (group) => {
    let totalEpisodes = 0;
    let totalDuration = 0; // in minutes
    let watchedEpisodes = 0;
    let watchedDuration = 0; // in minutes
    let totalScore = 0;
    let scoredAnimeCount = 0;

    group.forEach(entry => {
        const episodes = getTotalEpisodes(entry); // Use proper function for unreleased anime
        const duration = entry.media?.duration || 24; // Default 24 min
        const adjustedDuration = duration >= 10 ? Math.max(0, duration - OP_ED_SKIP_MINUTES) : duration; // Subtract OP/ED skip only for ≥10 min eps
        const progress = entry.progress || 0; // Episodes watched

        totalEpisodes += episodes;
        totalDuration += episodes * adjustedDuration;

        // Calculate watched time based on progress
        watchedEpisodes += progress;
        watchedDuration += progress * adjustedDuration;

        // Calculate average score (only count anime with scores)
        const score = entry.media?.averageScore || entry.score || 0;
        if (score > 0) {
            totalScore += score;
            scoredAnimeCount++;
        }
    });

    const totalHours = Math.round(totalDuration / 60 * 10) / 10;
    const watchedHours = Math.round(watchedDuration / 60 * 10) / 10;
    const remainingHours = Math.round((totalDuration - watchedDuration) / 60 * 10) / 10;
    const averageScore = scoredAnimeCount > 0 ? Math.round(totalScore / scoredAnimeCount) : 0;

    return {
        animeCount: group.length,
        totalEpisodes,
        totalDuration, // minutes
        totalHours, // rounded to 1 decimal
        watchedEpisodes,
        watchedDuration, // minutes
        watchedHours, // rounded to 1 decimal
        remainingHours, // rounded to 1 decimal
        averageScore // rounded to nearest integer
    };
};

/**
 * Build anime groups from user's list
 * @param {Array} animeList - User's anime list entries
 * @param {Object} relationsData - Relations data from API
 * @returns {Array} Array of groups with metadata
 */
export const buildAnimeGroups = (animeList, relationsData) => {
    if (!animeList || animeList.length === 0) {
        return [];
    }

    // Build relation graph (already deduplicates)
    const { graph, animeMap } = buildRelationGraph(animeList, relationsData);

    // Find connected components
    const componentGroups = findConnectedComponents(graph);

    // Build group objects
    const groups = componentGroups.map(mediaIds => {
        // Deduplicate mediaIds just in case
        const uniqueMediaIds = [...new Set(mediaIds)];

        // Map to entries and filter out any nulls
        const entries = uniqueMediaIds
            .map(id => animeMap[id])
            .filter(Boolean);

        // Ensure unique entries (shouldn't be necessary but extra safety)
        const uniqueEntries = Array.from(
            new Map(entries.map(entry => [entry.media.id, entry])).values()
        );

        const sorted = sortGroupByReleaseDate(uniqueEntries);
        const stats = calculateGroupStats(sorted);

        return {
            id: `group_${sorted[0]?.media?.id || Math.random()}`,
            name: getGroupName(sorted),
            anime: sorted,
            stats,
            firstReleaseDate: sorted[0]?.media?.startDate,
            status: sorted[0]?.status // Status of earliest anime
        };
    });

    // Remove any empty groups and deduplicate by group ID
    const validGroups = groups.filter(g => g.anime && g.anime.length > 0);
    const uniqueGroups = Array.from(
        new Map(validGroups.map(group => [group.id, group])).values()
    );

    // Sort groups by first release date
    return uniqueGroups.sort((a, b) => {
        const dateA = parseDate(a.firstReleaseDate);
        const dateB = parseDate(b.firstReleaseDate);
        return dateA - dateB;
    });
};

/**
 * Detect pending anime (related but not in user's list)
 * @param {Array} userAnimeList - User's anime entries
 * @param {Object} allRelations - All relations data
 * @returns {Array} Pending anime with relation info
 */
export const detectPendingAnime = (userAnimeList, allRelations, titlePreference = null) => {
    const userAnimeIds = new Set(userAnimeList.map(entry => entry.media.id));
    const pendingAnime = [];
    const seenPending = new Set();


    // Removed anime filtering has been removed
    const removedAnimes = [];


    Object.keys(allRelations).forEach(mediaId => {
        const relations = allRelations[mediaId]?.relations?.edges || [];
        const sourceAnime = userAnimeList.find(e => e.media.id === parseInt(mediaId));

        relations.forEach(edge => {
            const relatedAnime = edge.node;
            const relatedId = relatedAnime.id;

            // Check if related anime is NOT in user's list and not already added
            if (!userAnimeIds.has(relatedId) && !seenPending.has(relatedId)) {
                // WHITELIST approach: Only allow anime types through
                // type === 'ANIME' is the primary check (covers all anime formats)
                // If type is present and NOT 'ANIME', skip it
                if (relatedAnime.type && relatedAnime.type !== 'ANIME') return;

                // Also filter by format using whitelist (in case type is missing)
                // Only allow known anime formats through
                const ANIME_FORMATS = ['TV', 'MOVIE', 'OVA', 'ONA', 'SPECIAL', 'TV_SHORT'];
                if (relatedAnime.format && !ANIME_FORMATS.includes(relatedAnime.format)) return;

                // If BOTH type and format are missing, skip - we can't confirm it's anime
                // This handles entries that couldn't be enriched (e.g. database not ready)
                if (!relatedAnime.type && !relatedAnime.format) return;

                // Filter out unwanted relation types for DISCOVER
                if (edge.relationType === 'CHARACTER') return;
                if (edge.relationType === 'SUMMARY') return;
                if (edge.relationType === 'COMPILATION') return;
                if (edge.relationType === 'OTHER') return;
                if (edge.relationType === 'ADAPTATION') return;

                // Filter out removed animes
                if (removedAnimes.includes(relatedId)) {
                    console.log(`Skipping anime ${relatedId} - in removed list`);
                    return;
                }

                pendingAnime.push({
                    ...relatedAnime,
                    relationType: edge.relationType,
                    relatedTo: sourceAnime?.media ? {
                        id: sourceAnime.media.id,
                        title: getDisplayTitle(sourceAnime.media, titlePreference)
                    } : null
                });

                seenPending.add(relatedId);
            }
        });
    });

    return pendingAnime;
};

/**
 * Get display-friendly relation type label
 * @param {string} relationType - Relation type from API
 * @returns {string} Display label
 */
export const getRelationTypeDisplay = (relationType) => {
    const relationMap = {
        'SEQUEL': 'Sequel',
        'PREQUEL': 'Prequel',
        'SIDE_STORY': 'Side Story',
        'PARENT': 'Parent Story',
        'ALTERNATIVE': 'Alternative',
        'SPIN_OFF': 'Spin-off',
        'ADAPTATION': 'Adaptation',
        'CHARACTER': 'Character',
        'SUMMARY': 'Summary',
        'COMPILATION': 'Compilation',
        'OTHER': 'Other',
        'RELATED': 'Related',
        'SOURCE': 'Source Material'
    };
    return relationMap[relationType] || relationType;
};

export const filterGroupsByStatus = (groups, status) => {
    if (!status || status === 'ALL') return groups;

    return groups.filter(group => {
        const statuses = group.anime.map(entry => entry.status);
        const statusCounts = {};
        statuses.forEach(s => {
            statusCounts[s] = (statusCounts[s] || 0) + 1;
        });

        const hasStatus = (s) => (statusCounts[s] || 0) > 0;
        const onlyStatuses = (...allowed) => statuses.every(s => allowed.includes(s));
        const allAreStatus = (s) => statuses.every(st => st === s);

        switch (status) {
            case 'PAUSED': // On Hold - HIGHEST PRIORITY
                // Show if ANY anime is PAUSED
                // Takes priority over CURRENT and PLANNING
                return hasStatus('PAUSED');

            case 'CURRENT': // Watching - SECOND PRIORITY
                // Show if ANY anime is CURRENT
                // But exclude if ANY anime is PAUSED (on-hold has priority)
                // Takes priority over PLANNING
                return hasStatus('CURRENT') && !hasStatus('PAUSED');

            case 'CONTINUE': // Continue Watching
                // Show groups with BOTH completed AND planning (may also have dropped)
                // These are series user has started but not finished
                return hasStatus('COMPLETED') &&
                    hasStatus('PLANNING') &&
                    onlyStatuses('COMPLETED', 'PLANNING', 'DROPPED');

            case 'HALF_COMPLETED':
                // Groups that have BOTH completed AND dropped, nothing else
                return hasStatus('COMPLETED') &&
                    hasStatus('DROPPED') &&
                    onlyStatuses('COMPLETED', 'DROPPED');

            case 'COMPLETED':
                // Only groups where ALL anime are COMPLETED (pure completion)
                return onlyStatuses('COMPLETED') &&
                    !hasStatus('PAUSED') &&
                    !hasStatus('CURRENT') &&
                    !hasStatus('PLANNING');

            case 'PLANNING':
                // Show if group has only PLANNING, or PLANNING+DROPPED (no completed/current/paused)
                return onlyStatuses('PLANNING', 'DROPPED') &&
                    hasStatus('PLANNING') &&
                    !hasStatus('COMPLETED');

            case 'DROPPED':
                // Show only if ALL anime are DROPPED
                return onlyStatuses('DROPPED');

            default:
                // Fallback: show if ANY anime has this status
                return hasStatus(status);
        }
    });
};

// Export utility functions
export { parseDate, formatReleaseDate, getDisplayTitle };

// ==================== MANGA GROUPING FUNCTIONS ====================

/**
 * Build a graph of manga relations (only connects manga-format entries)
 */
const buildMangaRelationGraph = (mangaList, relationsData) => {
    const graph = {};
    const mangaMap = {};

    // Build manga map
    mangaList.forEach(entry => {
        const mediaId = entry.media.id;
        if (!mangaMap[mediaId]) {
            mangaMap[mediaId] = entry;
            graph[mediaId] = new Set();
        }
    });

    // Add edges — filter OUT anime formats, KEEP manga formats
    Object.keys(relationsData).forEach(mediaId => {
        const relations = relationsData[mediaId]?.relations?.edges || [];

        relations.forEach(edge => {
            const relatedId = edge.node.id;
            const relationType = edge.relationType;
            const relatedFormat = edge.node.format;

            // Filter out unwanted relation types
            if (['CHARACTER', 'SUMMARY', 'COMPILATION', 'OTHER'].includes(relationType)) return;

            // Filter out anime formats (keep manga/novel/one_shot)
            const ANIME_FORMATS = ['TV', 'MOVIE', 'OVA', 'ONA', 'SPECIAL', 'TV_SHORT', 'MUSIC'];
            if (relatedFormat && ANIME_FORMATS.includes(relatedFormat)) return;

            // Only add edge if BOTH manga are in user's list
            if (mangaMap[mediaId] && mangaMap[relatedId]) {
                graph[mediaId].add(relatedId);
                graph[relatedId].add(mediaId);
            }
        });
    });

    return { graph, animeMap: mangaMap };
};

/**
 * Calculate manga group statistics (chapters/volumes instead of episodes/duration)
 */
export const calculateMangaGroupStats = (group, latestChapterMap = null) => {
    let totalChapters = 0;
    let readChapters = 0;
    let totalVolumes = 0;
    let readVolumes = 0;
    let totalScore = 0;
    let scoredCount = 0;

    group.forEach(entry => {
        const chapters = getTotalChapters(entry, latestChapterMap);
        const volumes = getTotalVolumes(entry);
        const chapterProgress = entry.progress || 0;
        const volumeProgress = entry.progressVolumes || 0;

        if (chapters !== null) {
            totalChapters += chapters;
        } else {
            // For ongoing manga without known total, use progress as minimum
            totalChapters += chapterProgress;
        }
        readChapters += chapterProgress;
        totalVolumes += volumes;
        readVolumes += volumeProgress;

        const score = entry.media?.averageScore || entry.score || 0;
        if (score > 0) {
            totalScore += score;
            scoredCount++;
        }
    });

    const remainingChapters = Math.max(0, totalChapters - readChapters);
    const remainingVolumes = Math.max(0, totalVolumes - readVolumes);

    return {
        mangaCount: group.length,
        animeCount: group.length, // Alias for compatibility with shared components
        totalChapters,
        readChapters,
        remainingChapters,
        totalVolumes,
        readVolumes,
        remainingVolumes,
        averageScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : 0,
        // Aliases for components that reference anime stat names
        totalEpisodes: totalChapters,
        watchedEpisodes: readChapters,
        totalHours: 0,
        watchedHours: 0,
        remainingHours: 0
    };
};

/**
 * Build manga groups from user's manga list
 */
export const buildMangaGroups = (mangaList, relationsData) => {
    if (!mangaList || mangaList.length === 0) {
        return [];
    }

    const { graph, animeMap: mangaMap } = buildMangaRelationGraph(mangaList, relationsData);
    const componentGroups = findConnectedComponents(graph);

    const groups = componentGroups.map(mediaIds => {
        const uniqueMediaIds = [...new Set(mediaIds)];
        const entries = uniqueMediaIds
            .map(id => mangaMap[id])
            .filter(Boolean);

        const uniqueEntries = Array.from(
            new Map(entries.map(entry => [entry.media.id, entry])).values()
        );

        const sorted = sortGroupByReleaseDate(uniqueEntries);
        const stats = calculateMangaGroupStats(sorted);

        return {
            id: `group_${sorted[0]?.media?.id || Math.random()}`,
            name: getGroupName(sorted),
            anime: sorted, // Keep "anime" key for component compatibility
            manga: sorted, // Also provide "manga" alias
            stats,
            firstReleaseDate: sorted[0]?.media?.startDate,
            status: sorted[0]?.status
        };
    });

    const validGroups = groups.filter(g => g.anime && g.anime.length > 0);
    const uniqueGroups = Array.from(
        new Map(validGroups.map(group => [group.id, group])).values()
    );

    return uniqueGroups.sort((a, b) => {
        const dateA = parseDate(a.firstReleaseDate);
        const dateB = parseDate(b.firstReleaseDate);
        return dateA - dateB;
    });
};

/**
 * Detect pending manga (related manga not in user's list)
 */
export const detectPendingManga = (userMangaList, allRelations, titlePreference = null) => {
    const userMangaIds = new Set(userMangaList.map(entry => entry.media.id));
    const pendingManga = [];
    const seenPending = new Set();

    Object.keys(allRelations).forEach(mediaId => {
        const relations = allRelations[mediaId]?.relations?.edges || [];
        const sourceManga = userMangaList.find(e => e.media.id === parseInt(mediaId));

        relations.forEach(edge => {
            const relatedMedia = edge.node;
            const relatedId = relatedMedia.id;

            if (!userMangaIds.has(relatedId) && !seenPending.has(relatedId)) {
                // WHITELIST: Only allow manga types through
                if (relatedMedia.type && relatedMedia.type !== 'MANGA') return;

                // Format whitelist for manga
                const MANGA_FORMATS_LOCAL = ['MANGA', 'NOVEL', 'ONE_SHOT'];
                if (relatedMedia.format && !MANGA_FORMATS_LOCAL.includes(relatedMedia.format)) return;

                // Skip if both type and format are missing
                if (!relatedMedia.type && !relatedMedia.format) return;

                // Filter out unwanted relation types
                if (['CHARACTER', 'SUMMARY', 'COMPILATION', 'OTHER', 'ADAPTATION'].includes(edge.relationType)) return;

                pendingManga.push({
                    ...relatedMedia,
                    relationType: edge.relationType,
                    relatedTo: sourceManga?.media ? {
                        id: sourceManga.media.id,
                        title: getDisplayTitle(sourceManga.media, titlePreference)
                    } : null
                });

                seenPending.add(relatedId);
            }
        });
    });

    return pendingManga;
};

/**
 * Filter manga groups by reading status
 */
export const filterMangaGroupsByStatus = (groups, status) => {
    if (!status || status === 'ALL') return groups;

    return groups.filter(group => {
        const entries = group.anime || group.manga || [];
        const statuses = entries.map(entry => entry.status);
        const statusCounts = {};
        statuses.forEach(s => {
            statusCounts[s] = (statusCounts[s] || 0) + 1;
        });

        const hasStatus = (s) => (statusCounts[s] || 0) > 0;
        const onlyStatuses = (...allowed) => statuses.every(s => allowed.includes(s));

        switch (status) {
            case 'PAUSED':
                return hasStatus('PAUSED');
            case 'CURRENT':
                return hasStatus('CURRENT') && !hasStatus('PAUSED');
            case 'CONTINUE':
                return hasStatus('COMPLETED') && hasStatus('PLANNING') &&
                    onlyStatuses('COMPLETED', 'PLANNING', 'DROPPED');
            case 'HALF_COMPLETED':
                return hasStatus('COMPLETED') && hasStatus('DROPPED') &&
                    onlyStatuses('COMPLETED', 'DROPPED');
            case 'COMPLETED':
                return onlyStatuses('COMPLETED') &&
                    !hasStatus('PAUSED') &&
                    !hasStatus('CURRENT') &&
                    !hasStatus('PLANNING');
            case 'PLANNING':
                return onlyStatuses('PLANNING', 'DROPPED') &&
                    hasStatus('PLANNING') && !hasStatus('COMPLETED');
            case 'DROPPED':
                return onlyStatuses('DROPPED');
            default:
                return hasStatus(status);
        }
    });
};
